import { useAppDispatch, useAppSelector } from "app/hooks";
import { getConfigs } from "app/reducers/configSlice";
import { getModalState, SESSION_MODAL_TYPES } from "app/reducers/modalSlice";
import { fetchConfigs } from "app/thunks/configAPI";
import { deleteMatch } from "app/thunks/matchesAPI";
import { getSessionUpdates, startSession } from "app/thunks/sessionsAPI";
import { endGame, startGame } from "app/thunks/sessionsAPI";
import { ConfirmationModal } from "features/confirmation-modal/ConfirmationModal";
import { Match } from "interfaces/Match";
import { SESSION_STATUS } from "interfaces/Sessions";
import React, { useEffect, useState } from "react";
import { BUTTON_TYPES, Title } from "ui";

import Session from "./Session";

export const Sessions = () => {
  const { configs, isFetching: configsFetching } = useAppSelector(getConfigs);
  const { sessionId, isOpen, extraData } = useAppSelector(getModalState);
  const dispatch = useAppDispatch();

  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isDeletingMatch, setIsDeletingMatch] = useState(false);
  const [isCancelingSession, setIsCancelingSession] = useState(false);
  const [sessionsData, setSessionsData] = useState<
    | { sessionId: string; matches: Match[]; status: SESSION_STATUS }[]
    | undefined
  >(undefined);

  const handleStartSession = async (configId: string) => {
    setIsActionLoading(true);
    try {
      await dispatch(startSession(configId)).unwrap();
      await dispatch(fetchConfigs()).unwrap();
    } catch (err) {
      console.log("ERROR CREATING SESSION", err);
    }
  };

  const handleStartGame = async (sessionId: string) => {
    setIsActionLoading(true);
    try {
      await dispatch(startGame({ sessionId })).unwrap();
      await dispatch(fetchConfigs()).unwrap();
    } catch (err) {
      console.log("ERROR STARTING THE GAME", err);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleEndGame = async () => {
    if (!sessionId) return;
    setIsActionLoading(true);
    try {
      await dispatch(endGame({ sessionId })).unwrap();
      setSessionsData((prevSessions) => {
        if (!prevSessions)
          return [{ sessionId, matches: [], status: SESSION_STATUS.COMPLETE }];

        const sessionIndex = prevSessions.findIndex(
          (item) => item.sessionId === sessionId,
        );

        if (sessionIndex !== -1) {
          const updatedSession = [...prevSessions];

          updatedSession[sessionIndex] = {
            sessionId,
            matches: [],
            status: SESSION_STATUS.COMPLETE,
          };

          return updatedSession;
        }
      });
      await dispatch(fetchConfigs()).unwrap();
    } catch (err) {
      console.log("ERROR ENDING THE GAME", err);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!sessionId) return;
    setIsCancelingSession(true);
    try {
      await dispatch(endGame({ sessionId, cancelSession: true })).unwrap();
      setSessionsData((prevSessions) => {
        if (!prevSessions) return prevSessions;
        return prevSessions.filter((item) => item.sessionId !== sessionId);
      });
      await dispatch(fetchConfigs()).unwrap();
    } catch (err) {
      console.log("ERROR ENDING THE SESSION", err);
    } finally {
      setIsCancelingSession(false);
    }
  };

  const handleDeleteMatch = async (matchId: string) => {
    setIsActionLoading(true);
    setIsDeletingMatch(true);
    try {
      await dispatch(deleteMatch(matchId)).unwrap();
      setSessionsData((prevSessions) => {
        if (!prevSessions) return prevSessions;
        return prevSessions.map((session) => {
          const updatedMatches = session.matches.filter(
            (match) => match._id !== matchId,
          );
          return {
            ...session,
            matches: updatedMatches,
            status: updatedMatches.length
              ? session.status
              : session.matches.some((match) => match._id === matchId)
                ? SESSION_STATUS.STANDBY
                : session.status,
          };
        });
      });
    } catch (err) {
      console.error(`Error deleting match ${matchId}:`, err);
    } finally {
      await dispatch(fetchConfigs()).unwrap();
      setIsActionLoading(false);
      setIsDeletingMatch(false);
    }
  };

  useEffect(() => {
    if (!configs.length) return;

    const sessionsIds = configs
      .filter(
        ({ sessions }) =>
          sessions[0] && sessions[0].status !== SESSION_STATUS.COMPLETE,
      )
      .map(({ sessions }) => sessions[0]._id);

    if (!sessionsIds.length) {
      setSessionsData([]);

      return;
    }

    const intervalId = setInterval(() => {
      if (isDeletingMatch || isCancelingSession) return;

      dispatch(getSessionUpdates(sessionsIds))
        .then((res: any) => {
          const { data } = res.payload;

          data.forEach((session: any) => {
            const { _id, matches, status } = session;

            if (status === SESSION_STATUS.COMPLETE) return;

            setSessionsData((prevSessions) => {
              if (!prevSessions) return [{ sessionId: _id, matches, status }];
              const sessionIndex = prevSessions.findIndex(
                (item) => item.sessionId === _id,
              );

              if (sessionIndex !== -1) {
                const existingSession = prevSessions[sessionIndex];

                const hasMatchesChanged = matches.some(
                  (match: Match, index: number) => {
                    const previousMatch = existingSession.matches[index];
                    return (
                      !previousMatch ||
                      match.headset[0]?.connectionId !==
                        previousMatch.headset[0]?.connectionId ||
                      match.headset[0]?.battery !==
                        previousMatch.headset[0]?.battery
                    );
                  },
                );

                const hasStatusChanged = status !== existingSession.status;

                if (hasMatchesChanged || hasStatusChanged) {
                  const updatedSession = [...prevSessions];
                  updatedSession[sessionIndex] = {
                    sessionId: _id,
                    matches,
                    status,
                  };
                  return updatedSession;
                }

                return prevSessions;
              }

              setIsActionLoading(false);

              return [...prevSessions, { sessionId: _id, matches, status }];
            });
          });
        })
        .catch((ex) => {
          console.error("There was an error loading the page", ex);
        });
    }, 1000);

    return () => {
      clearInterval(intervalId);

      // Clear any residual intervals
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      const interval_id = window.setInterval(() => {}, Number.MAX_SAFE_INTEGER);
      for (let i = 1; i < interval_id; i++) {
        window.clearInterval(i);
      }
    };
  }, [configs, dispatch, isDeletingMatch, isCancelingSession]);

  useEffect(() => {
    dispatch(fetchConfigs());
  }, [dispatch]);

  return (
    <>
      <div className="flex-col min-h-screen px-8 pt-8 bg-faux-dark-gunmetal w-full overflow-hidden">
        <Title text="Nueva partida" className="pb-4" />
        <div className="flex flex-col gap-4 self-center max-h-full">
          {configs?.map((config, index) => {
            return (
              <Session
                key={config._id}
                config={config}
                index={index}
                sessionsData={sessionsData}
                configsFetching={configsFetching}
                isActionLoading={isActionLoading}
                handleDeleteMatch={handleDeleteMatch}
                handleStartSession={handleStartSession}
                handleStartGame={handleStartGame}
              />
            );
          })}
        </div>
      </div>
      {isOpen && extraData?.sessionModalType === SESSION_MODAL_TYPES.CANCEL && (
        <ConfirmationModal
          closeButtonType={BUTTON_TYPES.CANCEL}
          confirmButtonType={BUTTON_TYPES.CONFIRM}
          closeText="Cancelar"
          confirmText="Confirmar"
          label="¿Realmente desea cancelar la sesión? Se perderán los datos cargados."
          title={"Cancelar sesión"}
          callback={handleEndSession}
        />
      )}
      {isOpen &&
        extraData?.sessionModalType === SESSION_MODAL_TYPES.FINALIZE && (
          <ConfirmationModal
            closeButtonType={BUTTON_TYPES.CANCEL}
            confirmButtonType={BUTTON_TYPES.CONFIRM}
            closeText="Cancelar"
            confirmText="Confirmar"
            label="¿Realmente desea finalizar todas las partidas de esta sesión?"
            title={"Finalizar partida(s)"}
            callback={handleEndGame}
          />
        )}
      {isOpen &&
        extraData?.sessionModalType === SESSION_MODAL_TYPES.LOW_BATTERY && (
          <ConfirmationModal
            closeButtonType={BUTTON_TYPES.CANCEL}
            confirmButtonType={BUTTON_TYPES.CONFIRM}
            closeText="Cancelar"
            confirmText="Confirmar"
            label="Alguno(s) de los cascos de la sesión cuentan con un nivel de batería menor al 10%. Es posible que no se complete la experiencia de juego ¿Desea continuar?"
            title={"Casco(s) con poca bateria"}
            callback={() => {
              handleStartGame(sessionId || "");
            }}
          />
        )}
    </>
  );
};
